import { z } from 'zod';
// import type { Pagination } from '../types/api';

// Validation result types
export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

export interface ValidationWarning {
  field: string;
  message: string;
  code: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  data?: any;
}

// Base response schemas
const PaginationSchema = z.object({
  current_page: z.number(),
  total_pages: z.number(),
  total_count: z.number(),
  per_page: z.number(),
  has_previous: z.boolean().optional(),
  has_next: z.boolean().optional(),
});

const BaseResponseSchema = z.object({
  success: z.boolean().optional(),
  message: z.string().optional(),
  errors: z.array(z.string()).optional(),
  pagination: PaginationSchema.optional(),
});

// Resource schemas
const UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  first_name: z.string(),
  last_name: z.string(),
  full_name: z.string(),
  role: z.enum(['viewer', 'operator', 'admin', 'super_admin', 'teacher']),
  active: z.boolean(),
  school_id: z.string().uuid().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

const StudentSchema = z.object({
  id: z.string().uuid(),
  student_id: z.string().optional(),
  first_name: z.string(),
  last_name: z.string(),
  date_of_birth: z.string(),
  gender: z.enum(['male', 'female', 'other', 'prefer_not_to_say']),
  current_class_id: z.string().uuid().optional(),
  enrollment_status: z.enum(['enrolled', 'pending', 'graduated', 'transferred', 'withdrawn', 'suspended']),
  active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

const CameraSchema = z.object({
  id: z.string().uuid(),
  area_id: z.string().uuid(),
  school_id: z.string().uuid(),
  name: z.string(),
  rtsp_url: z.string(),
  status: z.enum(['active', 'inactive', 'maintenance', 'error']),
  health_status: z.enum(['healthy', 'warning', 'error', 'offline', 'unknown']),
  active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

// Schema map by resource type
const schemaMap: Record<string, z.ZodSchema> = {
  user: UserSchema,
  users: z.array(UserSchema),
  student: StudentSchema,
  students: z.array(StudentSchema),
  camera: CameraSchema,
  cameras: z.array(CameraSchema),
  pagination: PaginationSchema,
};

// Main validation function
export function validateApiResponse(
  response: unknown,
  expectedSchema?: z.ZodSchema,
  resourceType?: string
): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  try {
    // If no schema provided, try to infer from resource type
    const schema = expectedSchema || (resourceType ? schemaMap[resourceType] : undefined);

    if (!schema) {
      warnings.push({
        field: 'schema',
        message: 'No validation schema available for this resource type',
        code: 'NO_SCHEMA',
      });
      return { isValid: true, errors, warnings, data: response };
    }

    // Validate response structure
    if (typeof response === 'object' && response !== null && 'data' in response) {
      // Handle wrapped responses
      const wrappedResponse = response as any;
      
      // Validate base response structure
      try {
        BaseResponseSchema.parse(wrappedResponse);
      } catch (e) {
        warnings.push({
          field: 'response',
          message: 'Response wrapper does not match expected structure',
          code: 'INVALID_WRAPPER',
        });
      }

      // Validate data content
      try {
        const parsed = schema.parse(wrappedResponse.data);
        return { isValid: true, errors, warnings, data: parsed };
      } catch (e) {
        if (e instanceof z.ZodError) {
          e.errors.forEach(err => {
            errors.push({
              field: err.path.join('.'),
              message: err.message,
              code: err.code,
            });
          });
        }
      }
    } else {
      // Direct validation
      try {
        const parsed = schema.parse(response);
        return { isValid: true, errors, warnings, data: parsed };
      } catch (e) {
        if (e instanceof z.ZodError) {
          e.errors.forEach(err => {
            errors.push({
              field: err.path.join('.'),
              message: err.message,
              code: err.code,
            });
          });
        }
      }
    }
  } catch (error) {
    errors.push({
      field: 'validation',
      message: error instanceof Error ? error.message : 'Unknown validation error',
      code: 'VALIDATION_ERROR',
    });
  }

  return { isValid: errors.length === 0, errors, warnings, data: response };
}

// Logging helper
export function logValidationResults(
  url: string,
  method: string,
  result: ValidationResult
): void {
  if (!result.isValid || result.warnings.length > 0) {
    console.group(`API Validation: ${method} ${url}`);
    
    if (result.errors.length > 0) {
      console.error('Validation Errors:', result.errors);
    }
    
    if (result.warnings.length > 0) {
      console.warn('Validation Warnings:', result.warnings);
    }
    
    console.groupEnd();
  }
}

// Export additional utilities
export function createCustomSchema(definition: z.ZodRawShape) {
  return z.object(definition);
}

export function registerResourceSchema(resourceType: string, schema: z.ZodSchema) {
  schemaMap[resourceType] = schema;
}